import styled, { DefaultTheme, css } from 'styled-components'

import { transparentize } from 'polished'

import { StyledFigure } from '~/components/Icon/style'

import { PulseAnimateMode, pulseAnimate } from '~/styles/shared/pulseAnimate'

import { HeightClass } from '~/utils/enum/classes'

import { ButtonVariant, ImpactClass, TextColor } from './types'

type StyledButtonProps = {
  variant: ButtonVariant
  heightClass: HeightClass
  impactClass: ImpactClass
  textColor: TextColor
  theme: DefaultTheme
  focusable: boolean
  disabled?: boolean
  $disabled?: boolean
  pulseAnimate?: PulseAnimateMode
  clicked?: boolean
  backgroundColor?: string
}

type StyledLinkButtonProps = StyledButtonProps & {
  $disabled?: boolean
}

const getBackgroundColor = ({
  theme,
  textColor,
  impactClass,
  variant,
  clicked
}: StyledButtonProps): string => {
  const impactClassColor = theme.button.textColors?.[textColor].impactClasses[
    impactClass
  ] as string

  if (clicked) {
    if (impactClass === 'navigation') {
      return theme.button.clickedBackground
    }

    return transparentize(0.7, impactClassColor)
  }
  if (variant === 'primary') {
    return impactClassColor
  }

  return theme.colors.defaultBackground
}

const getColor = ({
  theme,
  textColor,
  impactClass,
  variant,
  clicked
}: StyledButtonProps) => {
  const impactClassColor = theme.button.textColors?.[textColor].impactClasses[
    impactClass
  ] as string
  if (clicked) {
    return impactClassColor
  }

  if (variant === 'primary') {
    return theme.button.textColors?.[textColor].color
  }

  return impactClassColor
}

const getFocusBorderColor = ({
  theme,
  textColor,
  impactClass,
  variant
}: StyledButtonProps) => {
  if (variant === 'primary') {
    return theme.button.focusBorderColor
  }

  return theme.button.textColors?.[textColor].impactClasses[impactClass]
}

const getDisabledBorderColor = ({
  theme,
  textColor,
  impactClass,
  variant
}: StyledButtonProps) => {
  if (variant === 'primary') {
    return theme.colors.defaultBackground
  }

  return theme.button.textColors?.[textColor].impactClasses[impactClass]
}

const getFocusBorderWidth = ({
  theme,
  heightClass,
  focusable,
  variant
}: StyledButtonProps) => {
  if (variant === 'secondary' && focusable) {
    const numberSize = theme.button.border?.[heightClass]?.replace('px', '')

    const size = !isNaN(numberSize) ? Number(numberSize) + 1 + 'px' : '4px'

    return size
  }

  return theme.button.border?.[heightClass]
}

const buttonBase = css<StyledButtonProps>`
  box-sizing: border-box;

  ${({ theme, textColor, impactClass, heightClass, clicked }) => css`
    border-width: ${theme.button.border?.[heightClass]};
    border-style: solid;

    border-color: ${clicked
      ? theme.button.focusBorderColor
      : theme.button.textColors?.[textColor].impactClasses[impactClass]};
  `};

  background-color: ${getBackgroundColor};
  color: ${getColor};

  font-weight: ${({ theme }) => theme.button.fontWeight};

  font-size: ${({ theme, heightClass }) => theme.button.fontSizes[heightClass]};

  padding: ${({ theme, heightClass }) => theme.button.padding[heightClass]};

  line-height: ${({ theme, heightClass }) =>
    theme.button.lineHeights[heightClass]};

  font-family: ${({ theme }) => theme.fonts?.barlow};

  border-radius: ${({ focusable, theme, heightClass }) =>
    focusable ? theme.button.borderRadius?.[heightClass] : '0px'};

  height: auto;
  min-height: ${({ theme, heightClass }) => theme.button.heights[heightClass]};

  width: 100%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.1s ease-in;

  &:focus {
    outline: none;
    border: ${getFocusBorderWidth} solid ${getFocusBorderColor};
  }

  &:disabled {
    border-style: dashed;
    border-color: ${getDisabledBorderColor};
    cursor: not-allowed;
  }

  > ${StyledFigure} {
    outline: none;
  }
`

const StyledButton = styled.button<StyledButtonProps>`
  ${buttonBase}

  ${({ pulseAnimate: mode, theme, textColor, impactClass, heightClass }) =>
    mode &&
    pulseAnimate(
      mode,
      theme.button.textColors?.[textColor].impactClasses[impactClass],
      theme.button.border?.[heightClass]
    )}
`

export const StyledLinkButton = styled.a<StyledLinkButtonProps>`
  ${buttonBase}

  text-decoration: none;
  text-align: center;

  &:hover {
    text-decoration: none;
    color: ${getColor};
  }

  ${({ $disabled }) =>
    $disabled &&
    css`
      border-style: dashed;
      border-color: ${getDisabledBorderColor};
      cursor: not-allowed;
      pointer-events: none;
    `}

  ${({ pulseAnimate: mode, theme, textColor, impactClass, heightClass }) =>
    mode &&
    pulseAnimate(
      mode,
      theme.button.textColors?.[textColor].impactClasses[impactClass],
      theme.button.border?.[heightClass]
    )}
`

export default StyledButton
